import {DateOnlyIsoString, DateTimeIsoString, MaterialType, StatusChipColors} from '@aviary/store/common/common.types';
import {enumCaseToTitleCase} from '@aviary/store/transformation.helpers';

export const HOLDS_FEATURE_KEY = 'holds';

export interface Hold {
  readonly id: number;

  readonly catalogRecordId: number;
  readonly catalogRecordIdentifier: string;

  readonly patronFirstName: string;
  readonly patronLastName: string;

  readonly queueIdx: number;
  readonly status: HoldStatus;
  readonly expireDate: DateOnlyIsoString;
  readonly pickupExpireDate: DateOnlyIsoString;
  readonly notWantedBefore: DateOnlyIsoString;
  readonly notWantedAfter: DateOnlyIsoString;
  readonly type: string;

  readonly materialType: MaterialType;
  readonly itemTitle: string;
  readonly coverImageUrl: string;

  readonly pickupBranchId: number;
  readonly pickupBranchName: string;
  readonly pickupBranchAddress: string;
  readonly pickupBranchPhone: string;

  readonly circulatingCopies: number;

  readonly placedDate: DateTimeIsoString;
  readonly modifiedDate: DateTimeIsoString;
}

export interface HoldDialogData {
  hold: Hold;
}

export const READY_FOR_PICKUP_HOLD_STATUSES = ['HELD'] as const;
export const ON_HOLD_HOLD_STATUSES = ['TRANSIT', 'PULLED', 'PENDING', 'REQUESTED', 'PAUSED'] as const;
export const ACTIVE_HOLD_STATUSES = [...READY_FOR_PICKUP_HOLD_STATUSES, ...ON_HOLD_HOLD_STATUSES] as const;
export const INACTIVE_HOLD_STATUSES = ['PICKED_UP', 'UNCLAIMED', 'NOT_SUPPLIED', 'CANCELED', 'EXPIRED'] as const;
export const ALL_HOLD_STATUSES = [...ACTIVE_HOLD_STATUSES, ...INACTIVE_HOLD_STATUSES] as const;

export type HoldStatus = (typeof ALL_HOLD_STATUSES)[number];

export const DISPLAYABLE_HOLD_STATUSES: Map<HoldStatus, string> = new Map(
  ALL_HOLD_STATUSES.map(status => {
      switch (status) {
        case 'TRANSIT':
          return [status, 'In Transit'];
        case 'PULLED':
          return [status, 'Located'];
      }

      return [status, enumCaseToTitleCase(status)];
    }
  )
);

export const HOLD_STATUS_CHIP_COLORS: StatusChipColors<HoldStatus> = {
  'status-positive': ['HELD'],
  'status-neutral': ['PULLED', 'REQUESTED'],
  'status-negative': ['UNCLAIMED', 'NOT_SUPPLIED'],
  'status-transitional': ['TRANSIT', 'PENDING'],
  'status-inactive': ['PAUSED', 'PICKED_UP', 'CANCELED', 'EXPIRED']
};

export type ReadyForPickupHolds = Record<number, Hold[]>; // Arrays of holds grouped by branch ID.

export interface ReadyForPickupGroup {
  readonly branchId: number;
  readonly holds: Hold[];
  readonly itemCount: string; // "N items"
}

export interface EditHoldValue {
  pickupBranch: number;
  pauseUntil: DateOnlyIsoString;
}

export interface AllHoldsDialogData {
  holds: Hold[];
  totalHoldsCount: number;
}

export interface PauseAllHoldsValue {
  pauseUntil: DateOnlyIsoString;
}
